import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { EmptyState } from "../components/EmptyState";
import { SidebarPanel } from "../components/SidebarPanel";
import { Button } from "../components/Button";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { useGetResellerUsersQuery, useRemoveResellerUserMutation } from "../query/usersQuery";
import { ResellerUserForm } from "./components/ResellerUserForm";
import type { ResellerId } from "@KassaroAB/shared/types/ids";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";

const ActionsCell = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
  align-items: center;
`;

const DangerButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

export const ResellerUsersView = () => {
  const { t } = useTranslation();
  const { resellerId } = useParams<{ resellerId: ResellerId }>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: users,
    isLoading,
    error,
  } = useGetResellerUsersQuery(resellerId ?? null);

  const {mutate: removeUserMutation, isPending, error: removeUserError} = useRemoveResellerUserMutation(resellerId ?? null);

  const handleAddUserSuccess = () => {
    setSuccessMessage(t("usersView.successAdded"));
    setIsPanelOpen(false);
  };

  const handleRemoveUser = (userId: string, userEmail: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("usersView.removeConfirm", { email: userEmail }))) {
      removeUserMutation(userId, {
        onSuccess: () => {
          setSuccessMessage(t("usersView.successRemoved"));
        },
        onError: () => {
          setSuccessMessage(null);
        },
      });
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoading) {
    return (
      <PageContent title={t("usersView.title")}>
        <LoadingContainer message={t("usersView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("usersView.title")}
      actions={<Button onClick={() => setIsPanelOpen(true)}>{t("usersView.addUser")}</Button>}
    >
      {error && (
        <InfoBox variant="error">
          {error?.message ?? t("usersView.errorLoad")}
        </InfoBox>
      )}

      {removeUserError && (
        <InfoBox variant="error">
          {removeUserError?.message ?? t("usersView.errorRemove")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {users && users.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("usersView.tableEmail")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableRole")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableCreated")}</TableHeaderCell>
                <TableHeaderCell>{t("usersView.tableActions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell noWrap>{user.email || "-"}</TableCell>
                  <TableCell noWrap>{user.name || "-"}</TableCell>
                  <TableCell noWrap>{user.role || "-"}</TableCell>
                  <TableCell noWrap>{formatDate(user.createdAt)}</TableCell>
                  <TableCell noWrap>
                    <ActionsCell>
                      <DangerButton
                        onClick={(e) =>
                          handleRemoveUser(user.id, user.email || t("usersView.thisUser"), e)
                        }
                        disabled={isPending}
                      >
                        {t("usersView.remove")}
                      </DangerButton>
                    </ActionsCell>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState message={t("usersView.empty")} />
        </Card>
      )}

      <SidebarPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        title={t("usersView.panelAddTitle")}
      >
        <ResellerUserForm
          resellerId={resellerId ?? null}
          onSuccess={handleAddUserSuccess}
          onCancel={handlePanelClose}
        />
      </SidebarPanel>
    </PageContent>
  );
};
